import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import AdminSidebar from "../../components/admin/AdminSidebar";
import AdminHeader from "../../components/admin/AdminHeader";
import "../../assets/css/admin/AdminTheme.css"; // theme variables (light/dark)
import "../../assets/css/admin/AdminLayout.css"; // layout shell
import "../../assets/css/admin/layout.css"; // global base + dark overrides

const ROUTE_LABELS = {
  "/admin/dashboard": { title: "Dashboard", sub: "Overview & analytics" },
  "/admin/sub-admin": { title: "Sub Admin", sub: "Manage sub admins" },
  "/admin/students": {
    title: "Students Management",
    subtitle: "Manage platform students",
  },
  "/admin/mentors": {
    title: "Mentor Management",
    subtitle: "Manage platform mentors",
  },
  "/admin/roadmaps": {
    title: "Roadmaps Management",
    sub: "Manage learning paths",
  },
  "/admin/courses": { title: "Courses", sub: "Course catalog" },
  "/admin/create-course": { title: "Create Course", sub: "Course builder" },
  "/admin/categories": { title: "Categories", sub: "Manage course categories" },
  "/admin/mentors/verification": {
    title: "Mentor Verification",
    sub: "Review applications",
  },
  "/admin/earnings": {
    title: "Earnings Management",
    sub: "Platform revenue and commission settings",
  },
  "/admin/withdrawals": {
    title: "Withdrawals",
    sub: "Manage mentor payout requests",
  },
  "/admin/ai-tutor": { title: "AI Tutor", sub: "AI configuration" },
  "/admin/analytics": { title: "Analytics", sub: "Data & insights" },
  "/admin/reports": { title: "Reports & Disputes", sub: "Review reports" },
  "/admin/settings": { title: "System Settings", sub: "Platform settings" },
  "/admin/profile": { title: "My Profile", sub: "Account settings" },
  "/admin/chat": { title: "Chat", sub: "Messages and conversations" },
};

const getPageInfo = (pathname) => {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  const match = Object.keys(ROUTE_LABELS)
    .filter((k) => pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return ROUTE_LABELS[match] || { title: "Admin Panel", sub: "Dashboard" };
};

/**
 * Read a CSS custom property from :root and convert to pixels.
 * Falls back to hardcoded values if the variable isn't available yet.
 */
const getSidebarWidth = (collapsed) => {
  const varName = collapsed ? "--as-width-collapsed" : "--as-width-expanded";
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();

  if (!raw) return collapsed ? 76 : 264; // safe fallback

  if (raw.endsWith("rem")) {
    const rootFz = parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );
    return parseFloat(raw) * rootFz;
  }
  return parseFloat(raw);
};

const getMode = () => {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1100) return "tablet";
  return "desktop";
};

const TRANSITION =
  "margin-left 0.28s cubic-bezier(0.4,0,0.2,1), width 0.28s cubic-bezier(0.4,0,0.2,1)";

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const pageInfo = getPageInfo(location.pathname);

  const [theme, setTheme] = useState(
    () => localStorage.getItem("admin-theme") || "light",
  );
  const [mode, setMode] = useState(() => getMode());
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  // ── Apply theme class to <html> so scrollbars + root bg match
  useEffect(() => {
    localStorage.setItem("admin-theme", theme);
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // ── Resize handler
  useEffect(() => {
    const handler = () => {
      const m = getMode();
      setMode(m);
      if (m === "desktop") setOverlayOpen(false);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── Close overlay on route change
  useEffect(() => {
    setOverlayOpen(false);
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => {
    if (mode === "desktop") setIsCollapsed((p) => !p);
    else setOverlayOpen((p) => !p);
  }, [mode]);

  const isDesktop = mode === "desktop";
  const isTablet = mode === "tablet";
  const isMobile = mode === "mobile";

  // On tablet the sidebar is always icon-only; on mobile it's overlaid (0 margin).
  const sidebarCollapsed = isDesktop ? isCollapsed : isTablet ? true : false;

  const marginLeft = isDesktop
    ? getSidebarWidth(isCollapsed)
    : isTablet
      ? getSidebarWidth(true)
      : 0;

  const showBackdrop = !isDesktop && overlayOpen;

  return (
    <div className={`admin-layout ${theme}`} data-theme={theme}>
      {showBackdrop && (
        <div
          className="sidebar-backdrop active"
          onClick={() => setOverlayOpen(false)}
        />
      )}

      <AdminSidebar
        isCollapsed={sidebarCollapsed}
        toggleSidebar={toggleSidebar}
        isMobileOpen={isMobile && overlayOpen}
        isTabletOpen={isTablet && overlayOpen}
        mode={mode}
        theme={theme}
      />

      {/*
        KEY FIX: admin-main receives marginLeft so it sits beside the fixed
        sidebar. No background is set here — admin-page-content is transparent,
        so each page component's own background shows through flush to the edges.
      */}
      <main
        className="admin-main"
        style={{
          marginLeft: `${marginLeft}px`,
          width: `calc(100% - ${marginLeft}px)`,
          transition: TRANSITION,
          backgroundColor: "var(--bg-main)",
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AdminHeader
          theme={theme}
          toggleTheme={() =>
            setTheme((p) => (p === "light" ? "dark" : "light"))
          }
          isSidebarCollapsed={isCollapsed}
          toggleSidebar={toggleSidebar}
          mode={mode}
          pageTitle={pageInfo.title}
          pageSubtitle={pageInfo.sub}
        />

        {/* Transparent wrapper — each page owns its background + padding */}
        <div
          className="admin-page-content "
          style={{
            flex: 1,
            height: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
